import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  AlertColor,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import { t } from 'i18next';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { getInvoiceDeliveryMethodStringText, InvoiceDeliveryMethodStringEnum } from 'src/shared/models/enums/invoiceDeliveryMethodString.enum';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { useParams } from 'react-router-dom';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { SendingSessionStatusEnum } from 'src/shared/models/enums/sendingSessionStatus.enum';
import { getSendingSessionTypeText, InvoiceSendingTypeStringEnum } from 'src/shared/models/enums/InvoiceSendingTypeString.enum';
import { useMemo } from 'react';
import { SendingSessionSectionBtnHandler } from './sendingSessionSectionBtnHandler';
import { convertSendingTypeIntToString } from './invoiceMissionSendingTypeConverter';

type SendingSessionSectionProps = {
  invoiceMission: InvoiceMissionResponse;
  loading: boolean;
  isFSVersion2: boolean;
  refetchMission: (refetch: boolean) => void;
};

export const SendingSessionSection = (props: SendingSessionSectionProps) => {
  const { invoiceMission, loading, isFSVersion2, refetchMission } = props;
  const { sendingType } = useParams<{ sendingType: string }>();
  const parsedSendingType = Number(sendingType);

  // Ta bort sendings med status "Created" från sendingSessions
  const sentSessions = invoiceMission.sendingSessions.filter((snd) => snd.status !== SendingSessionStatusEnum.Created);
  const totalSentInvoices = invoiceMission.sendingSessions
    .filter((sending) => sending.status !== SendingSessionStatusEnum.Created)
    .reduce((sum, sending) => sum + (sending.sendingsCount || 0), 0);

  const kind = useMemo(() => convertSendingTypeIntToString(parsedSendingType), [parsedSendingType]);
  const data = useMemo(() => invoiceMission.dataList?.find((d) => d.sendingType === kind), [invoiceMission.dataList, kind]);

  const approval = data?.approval;

  const isRejected = Boolean(approval?.approvalRequired && approval?.deniedAt);
  const deniedAt = isRejected ? DateHelper.getDateTime(approval?.deniedAt) : '';
  const deniedBy = isRejected ? approval?.deniedBy ?? '' : '';

  const isPendingApproval =
    parsedSendingType !== InvoiceSendingTypeIntegerEnum.NotSet && Boolean(approval?.approvalRequired && !approval?.approvedAt && !approval?.deniedAt);

  const getSendingCompleteStatusMessage = () => {
    if (parsedSendingType === InvoiceSendingTypeIntegerEnum.Main) {
      return t('invoice:invoiceMissionDetailTabs.mainSending');
    }
    if (parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder1) {
      return t('invoice:invoiceMissionDetailTabs.reminderOne');
    }
    if (parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder2) {
      return t('invoice:invoiceMissionDetailTabs.reminderTwo');
    }
    return '';
  };

  const sendingLabel = getSendingCompleteStatusMessage() || t('invoice:invoiceMissionPage.sendingGenericName');

  const getSeverityAndAlertTitle = (): {
    severity: AlertColor;
    alertTitle: string;
  } => {
    if (isRejected) {
      return {
        severity: 'error',
        alertTitle: t('invoice:invoiceMissionPage.rejectedStatusMessage', {
          sending: sendingLabel,
          deniedAt,
          deniedBy,
        }),
      };
    }

    if (isPendingApproval) {
      return { severity: 'info', alertTitle: t('invoice:invoiceMissionPage.waitingApprove') };
    }

    if (!invoiceMission || parsedSendingType === InvoiceSendingTypeIntegerEnum.NotSet) {
      return { severity: 'info', alertTitle: t('invoice:invoiceMissionPage.sendingSessionOverview') };
    }

    if (sentSessions.length === 0) {
      return {
        severity: 'info',
        alertTitle: t('invoice:invoiceMissionPage.noSendingsAtm'),
      };
    }

    if (totalSentInvoices > 0 && totalSentInvoices < invoiceMission.data.totalCount) {
      return {
        severity: 'warning',
        alertTitle: t('invoice:invoiceMissionPage.sendingNotFullfilled', {
          totalSentInvoices,
          totalInvoices: invoiceMission.data.totalCount,
        }),
      };
    }

    if (totalSentInvoices >= invoiceMission.data.totalCount) {
      return {
        severity: 'success',
        alertTitle: t('invoice:invoiceMissionPage.sendingCompleteWithType', { reminder: getSendingCompleteStatusMessage() }),
      };
    }
    return {
      severity: 'info',
      alertTitle: '',
    };
  };
  const { severity, alertTitle } = getSeverityAndAlertTitle();

  const displayApproval = () => {
    if (approval) {
      const isApproved = approval.approvalRequired && approval.approvedAt && approval.approvedBy;

      if (isApproved) {
        return (
          <Alert
            severity='success'
            sx={{ mb: -1, border: (thm) => `1px solid ${thm.palette.divider}`, '& .MuiAlert-message': { width: '100% !important' } }}
          >
            {t('invoice:invoiceMissionPage.alertIsApprovedBy', {
              approvedBy: approval.approvedBy,
              approvedAt: DateHelper.getDateTime(approval.approvedAt),
              deadline: DateHelper.getDate(approval.deadline),
            })}
          </Alert>
        );
      }
    }

    return null;
  };

  if (loading) {
    return (
      <GridWrapper>
        <Grid item xs={12}>
          <Skeleton variant='rounded' height={48} sx={{ mt: 2 }} />
          <Skeleton variant='rounded' height={20} width='60%' sx={{ mt: 2 }} />
          <Skeleton variant='rounded' height={20} width='80%' sx={{ mt: 1 }} />
          <Skeleton variant='rounded' height={20} width='70%' sx={{ mt: 1 }} />
        </Grid>
      </GridWrapper>
    );
  }

  return (
    <GridWrapper>
      <Grid item xs={12}>
        {isFSVersion2 ? (
          <>
            {displayApproval()}
            {parsedSendingType !== InvoiceSendingTypeIntegerEnum.NotSet && (
              <Alert
                severity={severity}
                sx={{ mt: 2, py: 1, border: (thm) => `1px solid ${thm.palette.divider}`, '& .MuiAlert-message': { width: '100% !important' } }}
              >
                <AlertTitle sx={{ mb: 0 }}>{alertTitle}</AlertTitle>
                {!isRejected && <SendingSessionSectionBtnHandler loading={loading} invoiceMission={invoiceMission} refetchMission={refetchMission} />}
              </Alert>
            )}

            {/* Sending log */}
            {sentSessions && sentSessions.length > 0 && (
              <TableContainer sx={{ mt: 1 }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, py: 0.75 }}>{t('invoice:invoiceMissionPage.quantity')}</TableCell>
                      {parsedSendingType === InvoiceSendingTypeIntegerEnum.NotSet && (
                        <TableCell sx={{ fontWeight: 500, py: 0.75 }}>{t('invoice:invoiceMissionPage.type')}</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 500, py: 0.75 }}>{t('invoice:invoiceMissionPage.method')}</TableCell>
                      <TableCell sx={{ fontWeight: 500, py: 0.75 }}>{t('invoice:invoiceMissionPage.paymentRate')}</TableCell>
                      <TableCell sx={{ fontWeight: 500, py: 0.75 }}>{t('invoice:invoiceMissionPage.date')}</TableCell>
                      <TableCell sx={{ fontWeight: 500, py: 0.75 }}>{t('invoice:invoiceMissionPage.sign')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sentSessions
                      .slice()
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((sending) => {
                        const paidPct = sending.sendingsCount > 0 ? Math.round((sending.paidCount / sending.sendingsCount) * 1000) / 10 : 0;
                        const getColor = (rate: number) => {
                          if (rate >= 85) return lighten('#00af83', 0.4);
                          if (rate >= 50) return lighten('#ff9800', 0.4);
                          return lighten('#e11f51', 0.4);
                        };

                        return (
                          <TableRow key={sending.session_id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant='caption'>
                                {t('invoice:invoiceMissionPage.invoiceCounter', { count: sending.sendingsCount })}
                              </Typography>
                            </TableCell>
                            {parsedSendingType === InvoiceSendingTypeIntegerEnum.NotSet && (
                              <TableCell sx={{ py: 0.75 }}>
                                <Typography variant='caption'>
                                  {getSendingSessionTypeText(sending.type as InvoiceSendingTypeStringEnum, t)}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant='caption'>
                                {getInvoiceDeliveryMethodStringText(sending.deliveryMethod as InvoiceDeliveryMethodStringEnum, t)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <LinearProgress
                                  variant='determinate'
                                  value={Math.min(paidPct, 100)}
                                  sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    width: 40,
                                    flexShrink: 0,
                                    backgroundColor: '#e8e8e8',
                                    '& .MuiLinearProgress-bar': { borderRadius: 2, backgroundColor: getColor(paidPct) },
                                  }}
                                />
                                <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                                  {sending.paidCount}/{sending.sendingsCount} ({paidPct}%)
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant='caption' sx={{ whiteSpace: 'nowrap' }}>
                                {DateHelper.getDateTime(sending.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.75, maxWidth: 100 }}>
                              <Tooltip title={sending.sign || ''} arrow>
                                <Typography
                                  variant='caption'
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'block',
                                    maxWidth: 100,
                                  }}
                                >
                                  {sending.sign}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          <Alert sx={{ padding: 2 }} severity='warning'>
            {t('invoice:invoiceMissionPage.missingSendingData')}
          </Alert>
        )}
      </Grid>
    </GridWrapper>
  );
};
